import _ from 'lodash';
import Immutable from 'seamless-immutable';
import DisplayUnitModel from './display-unit-model';
import RefData from '../../ref-data';
import { logger } from '../../common/Services';
import { baseCollection, filter } from '../../common/collections';

/**
 * Transforms and array of backend displayUnit objects into a DisplayUnitCollection
 * @param {Object[]} displayUnits - backend formatted displayUnits
 * @returns {DisplayUnit[]}
 */
function deserialize(displayUnits = []) {
  return Immutable(_.map(displayUnits, DisplayUnitModel.deserialize));
}

/**
 * Transforms a DisplayUnitCollection into an array of backend displayUnit objects
 * @param {DisplayUnit[]} displayUnits
 * @returns {Object[]}
 */
function serialize(displayUnits) {
  return _.map(displayUnits, (displayUnit) => DisplayUnitModel.serialize(displayUnit));
}

/**
 * Takes a DisplayUnitCollection and filters them by the provided channel
 * @param {RefData|string} publishFormatTypeRefOrId - the format type ref to filter the display units by
 * @param {DisplayUnit[]} displayUnits - the display units to filter
 * @returns {DisplayUnit[]}
 */
function filterByPublishFormatTypeRef(publishFormatTypeRefOrId, displayUnits) {
  const publishFormatTypeRef = _.isString(publishFormatTypeRefOrId)
    ? RefData.Model.hydrate(publishFormatTypeRefOrId)
    : publishFormatTypeRefOrId;
  return filter((displayUnit) => displayUnit.typeRef.formatType === publishFormatTypeRef.id, displayUnits);
}

function filterByDestinationType({ destinationType }, displayUnits) {
  return filter((displayUnit) => {
    let result;
    switch (destinationType.id) {
      case RefData.Enum.TYPE.DESTINATION.STORY:
        result = displayUnit.isStoryUnit;
        break;
      case RefData.Enum.TYPE.DESTINATION.WEB:
        result = displayUnit.isWebUnit;
        break;
      case RefData.Enum.TYPE.DESTINATION.MICROSITE:
        result = displayUnit.isMicrosite;
        break;
      case RefData.Enum.TYPE.DESTINATION.OVERLAY:
        result = displayUnit.isOverlay;
        break;
      default:
        logger.warn(`Unknown destination type: ${destinationType.id}`);
        return false;
    }
    return result;
  }, displayUnits);
}

/**
 * This method is used for filtering the display units by the experience formatType id and the destination type
 * @param {Object} [
 *  {String} destinationType - type of the destination, where a display unit can be deployed to, like 'web-embeded',
 *  {String} experienceFormatId - format of the experience, can be 'format.micro', 'format.macro' or 'format.all'
 * @param {Array} displayUnits -  list of displayUnits objects, recieved from the backend
 * @returns {Array} displayUnits <description>
 */
function filterByFormatIdAndDestinationType({ destinationType, experienceFormatTypeId }, displayUnits) {
  const filteredByFormat = filter(
    (displayUnit) =>
      displayUnit.validFor === experienceFormatTypeId || displayUnit.validFor === RefData.Enum.FORMAT.DISPLAY.ALL,
    displayUnits
  );
  return filterByDestinationType({ destinationType }, filteredByFormat);
}

const findDestinationTypeByEmbedType = (embedType, destinationType, experienceFormatTypeId, displayUnits) => {
  const filterdDestinationTypes = filterByFormatIdAndDestinationType(
    { destinationType, experienceFormatTypeId },
    displayUnits
  );

  return _.find(filterdDestinationTypes, (destination) => destination.embedType === embedType);
};

export default {
  ...baseCollection,
  create: deserialize,
  deserialize,
  serialize,

  filterByPublishFormatTypeRef,
  filterByDestinationType,
  filterByFormatIdAndDestinationType,
  findDestinationTypeByEmbedType,
};
